import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Autocomplete,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Pagination,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip as MuiTooltip,
  Typography
} from '@mui/material';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Database as DatabaseIcon,
  Gauge,
  HelpCircle,
  PlugZap,
  Square,
  Timer,
  Trash2,
  Users
} from 'lucide-react';
import api from '../services/api';

const PERIODI = ['giornaliero', 'settimanale', 'mensile'];
const ITALY_TIME_ZONE = 'Europe/Rome';

const CARD_ELEVATION_SX = {
  borderColor: 'rgba(145, 158, 171, 0.24)',
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.16), 0 12px 24px -4px rgba(145, 158, 171, 0.08)',
  transition: 'box-shadow 0.2s, transform 0.1s, border-color 0.2s',
  '&:hover': {
    boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.3), 0 16px 32px -4px rgba(145, 158, 171, 0.28)',
    transform: 'translateY(-1px)'
  }
};

function safeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function parseMysqlDatetimeUtc(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const s = String(value).trim();
  if (/t/i.test(s)) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d;
  }
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (!m) return null;
  const [, y, mo, d, hh = '00', mm = '00', ss = '00'] = m;
  return new Date(`${y}-${mo}-${d}T${hh}:${mm}:${ss}Z`);
}

function formatItalyDateTime(date, withSeconds = false) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('it-IT', {
    timeZone: ITALY_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: withSeconds ? '2-digit' : undefined
  }).format(date);
}

function formatBucket(bucket, periodo) {
  if (!bucket) return '';
  const s = String(bucket).trim();
  const date = parseMysqlDatetimeUtc(s);
  if (!date) return s;

  if (periodo === 'giornaliero') {
    const label = new Intl.DateTimeFormat('it-IT', {
      timeZone: ITALY_TIME_ZONE,
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
    const hh = String(label).split(',')[1]?.trim() || label;
    const dm = String(label).split(',')[0]?.trim() || '';
    return dm && hh ? `${dm} ${hh}` : label;
  }

  return new Intl.DateTimeFormat('it-IT', {
    timeZone: ITALY_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
}

function formatDurationSeconds(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h} ore ${m} minuti`;
  if (m > 0) return `${m} minuti ${sec} secondi`;
  return `${sec} secondi`;
}

function computeStatus(snapshot) {
  if (!snapshot) return { label: 'N/D', color: 'default', reasons: [] };
  if (snapshot.note) {
    return {
      label: 'error',
      color: 'error',
      reasons: [String(snapshot.note)]
    };
  }

  const lat = safeNumber(snapshot.latenza_ms);
  const pool = Math.max(safeNumber(snapshot.pool_dimensione), 0);
  const used = Math.max(safeNumber(snapshot.connessioni_attive), 0);
  const usage = pool > 0 ? used / pool : 0;
  const slowDelta = safeNumber(snapshot.query_lente_delta);

  const reasons = [];
  if (pool > 0 && usage > 0.8) {
    const pct = Math.round(usage * 100);
    reasons.push(`Pool alto (${pct}%: ${used}/${pool})`);
  }
  if (lat > 200) {
    reasons.push(`Latenza alta (${Math.round(lat)} ms)`);
  }
  if (slowDelta > 0) {
    reasons.push(`Slow queries Δ > 0 (${slowDelta})`);
  }

  if (reasons.length) return { label: 'warn', color: 'warning', reasons };
  return { label: 'ok', color: 'success', reasons: ['Nessuna anomalia rilevata'] };
}

function Stat({ label, value, icon: Icon, iconColor }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        {Icon ? <Icon size={14} color={iconColor || '#64748b'} /> : null}
        <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
  );
}

function MetricCard({ title, snapshot }) {
  const status = computeStatus(snapshot);
  const pool = safeNumber(snapshot?.pool_dimensione);
  const used = safeNumber(snapshot?.connessioni_attive);
  const progress = pool > 0 ? Math.min(Math.max((used / pool) * 100, 0), 100) : 0;
  const rilevatoItaly = snapshot?.rilevato_il ? formatItalyDateTime(parseMysqlDatetimeUtc(snapshot.rilevato_il), true) : '';

  return (
    <Card
      variant="outlined"
      sx={{
        ...CARD_ELEVATION_SX,
        p: 2,
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
        <MuiTooltip
          title={
            <Box sx={{ display: 'grid', gap: 0.5 }}>
              {(status.reasons || []).map((text) => (
                <Typography key={text} variant="caption">
                  {text}
                </Typography>
              ))}
            </Box>
          }
        >
          <Chip
            size="small"
            label={status.label}
            color={status.color}
            variant={status.color === 'default' ? 'outlined' : 'filled'}
          />
        </MuiTooltip>
      </Box>

      <Box sx={{ display: 'grid', gap: 0.75 }}>
        <Stat
          label="Connessioni attive"
          value={snapshot ? safeNumber(snapshot.connessioni_attive) : '-'}
          icon={Users}
          iconColor="#1976d2"
        />
        <Stat
          label="QPS"
          value={snapshot ? safeNumber(snapshot.query_al_secondo).toFixed(2) : '-'}
          icon={Gauge}
          iconColor="#9c27b0"
        />
        <Stat
          label="Latenza (ms)"
          value={snapshot ? safeNumber(snapshot.latenza_ms) : '-'}
          icon={Timer}
          iconColor="#ed6c02"
        />
        <Stat
          label="Slow queries Δ"
          value={snapshot ? safeNumber(snapshot.query_lente_delta) : '-'}
          icon={AlertTriangle}
          iconColor="#d32f2f"
        />
        <Stat
          label="Rilevato (Italia)"
          value={rilevatoItaly || '-'}
          icon={Activity}
          iconColor="#2e7d32"
        />
      </Box>

      <Box sx={{ mt: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Pool
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {pool > 0 ? `${used}/${pool}` : '-'}
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 99 }} />
      </Box>

      {snapshot?.note ? (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
          {String(snapshot.note)}
        </Typography>
      ) : null}
    </Card>
  );
}

function ChartCard({ title, onHelp, children }) {
  return (
    <Card variant="outlined" sx={{ ...CARD_ELEVATION_SX, p: 2, borderRadius: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
        {onHelp ? (
          <MuiTooltip title="Info">
            <IconButton size="small" onClick={onHelp}>
              <HelpCircle size={16} />
            </IconButton>
          </MuiTooltip>
        ) : null}
      </Box>
      <Box sx={{ width: '100%', height: 280 }}>{children}</Box>
    </Card>
  );
}

function KpiCard({ title, value, subtitle, accentColor, loading, onHelp }) {
  return (
    <Card
      variant="outlined"
      sx={{
        ...CARD_ELEVATION_SX,
        p: 2,
        borderRadius: 2,
        height: '100%',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 3, bgcolor: accentColor }} />
      {onHelp ? (
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <MuiTooltip title="Info">
            <IconButton size="small" onClick={onHelp}>
              <HelpCircle size={16} />
            </IconButton>
          </MuiTooltip>
        </Box>
      ) : null}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
        {loading ? '-' : value}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Card>
  );
}

export default function DbMonitor() {
  const [helpState, setHelpState] = useState({ open: false, title: '', content: null });
  const [cleanupStatus, setCleanupStatus] = useState(null);
  const [sezione, setSezione] = useState('monitor');
  const [liveLoading, setLiveLoading] = useState(true);
  const [liveError, setLiveError] = useState('');
  const [live, setLive] = useState({ principale: null, remoto: null });

  const [periodo, setPeriodo] = useState('giornaliero');
  const [storicoLoading, setStoricoLoading] = useState(true);
  const [storicoError, setStoricoError] = useState('');
  const [storico, setStorico] = useState({ principale: [], remoto: [] });

  const [slowLoading, setSlowLoading] = useState(true);
  const [slowError, setSlowError] = useState('');
  const [slow, setSlow] = useState({ principale: [], remoto: [] });

  const [capacityLoading, setCapacityLoading] = useState(true);
  const [capacityError, setCapacityError] = useState('');
  const [capacity, setCapacity] = useState(null);

  const [longConnLoading, setLongConnLoading] = useState(true);
  const [longConnError, setLongConnError] = useState('');
  const [longConn, setLongConn] = useState({ principale: null, remoto: null });
  const [killState, setKillState] = useState({ open: false, db: '', id: 0, mode: 'connection' });
  const [bulkKillState, setBulkKillState] = useState({ open: false, db: '', ids: [], view: 'running' });
  const [killLoading, setKillLoading] = useState(false);
  const [killMessage, setKillMessage] = useState('');
  const [longConnPages, setLongConnPages] = useState({
    principale: { running: 1, sleeping: 1 },
    remoto: { running: 1, sleeping: 1 }
  });
  const [longConnView, setLongConnView] = useState({
    principale: 'running',
    remoto: 'running'
  });

  const emailLogsPageSize = 25;
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [emailLogsError, setEmailLogsError] = useState('');
  const [emailLogsRows, setEmailLogsRows] = useState([]);
  const [emailLogsTotal, setEmailLogsTotal] = useState(0);
  const [emailLogsPage, setEmailLogsPage] = useState(1);
  const [emailLogsOnlyErrors, setEmailLogsOnlyErrors] = useState(true);
  const [emailLogsTraceIdInput, setEmailLogsTraceIdInput] = useState('');
  const [emailLogsTraceId, setEmailLogsTraceId] = useState('');

  const registroPageSize = 25;
  const [registroLoading, setRegistroLoading] = useState(false);
  const [registroError, setRegistroError] = useState('');
  const [registroRows, setRegistroRows] = useState([]);
  const [registroTotal, setRegistroTotal] = useState(0);
  const [registroPage, setRegistroPage] = useState(1);

  const [registroSearchInput, setRegistroSearchInput] = useState('');
  const [registroSearch, setRegistroSearch] = useState('');
  const [registroFunzionalitaInput, setRegistroFunzionalitaInput] = useState('');
  const [registroFunzionalita, setRegistroFunzionalita] = useState('');
  const [registroOperazioneInput, setRegistroOperazioneInput] = useState('');
  const [registroOperazione, setRegistroOperazione] = useState('');
  const [registroUtenteInput, setRegistroUtenteInput] = useState('');
  const [registroUtente, setRegistroUtente] = useState('');
  const [registroDataDaInput, setRegistroDataDaInput] = useState('');
  const [registroDataDa, setRegistroDataDa] = useState('');
  const [registroDataAInput, setRegistroDataAInput] = useState('');
  const [registroDataA, setRegistroDataA] = useState('');

  const [registroSortBy, setRegistroSortBy] = useState('creato_il');
  const [registroSortDir, setRegistroSortDir] = useState('DESC');
  const [registroFiltriLoading, setRegistroFiltriLoading] = useState(false);
  const [registroFiltri, setRegistroFiltri] = useState({ funzionalita: [], operazioni: [], utenti: [] });

  const fetchCleanupStatus = useCallback(async () => {
    try {
      const res = await api.get('/job-logs/db-conn-cleanup-status');
      setCleanupStatus(res.data || null);
    } catch {
      setCleanupStatus(null);
    }
  }, []);

  const fetchLive = useCallback(async () => {
    setLiveError('');
    try {
      const res = await api.get('/db-monitor/live');
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      const principal = rows.find((r) => String(r.nome_database || '').toLowerCase() === 'principale') || null;
      const remote = rows.find((r) => String(r.nome_database || '').toLowerCase() === 'remoto') || null;
      setLive({ principale: principal, remoto: remote });
    } catch (e) {
      setLiveError(e?.response?.data?.message || e?.message || 'Errore caricamento live');
    } finally {
      setLiveLoading(false);
    }
  }, []);

  const fetchStorico = useCallback(
    async (p) => {
      setStoricoError('');
      setStoricoLoading(true);
      try {
        const [a, b] = await Promise.all([
          api.get('/db-monitor/storico', { params: { db: 'principale', periodo: p } }),
          api.get('/db-monitor/storico', { params: { db: 'remoto', periodo: p } })
        ]);
        setStorico({
          principale: Array.isArray(a.data?.data) ? a.data.data : [],
          remoto: Array.isArray(b.data?.data) ? b.data.data : []
        });
      } catch (e) {
        setStoricoError(e?.response?.data?.message || e?.message || 'Errore caricamento storico');
      } finally {
        setStoricoLoading(false);
      }
    },
    []
  );

  const fetchSlow = useCallback(
    async (p) => {
      setSlowError('');
      setSlowLoading(true);
      try {
        const [a, b] = await Promise.all([
          api.get('/db-monitor/slow-queries', { params: { db: 'principale', periodo: p } }),
          api.get('/db-monitor/slow-queries', { params: { db: 'remoto', periodo: p } })
        ]);
        setSlow({
          principale: Array.isArray(a.data?.data) ? a.data.data : [],
          remoto: Array.isArray(b.data?.data) ? b.data.data : []
        });
      } catch (e) {
        setSlowError(e?.response?.data?.message || e?.message || 'Errore caricamento slow queries');
      } finally {
        setSlowLoading(false);
      }
    },
    []
  );

  const fetchCapacity = useCallback(
    async (p) => {
      setCapacityError('');
      setCapacityLoading(true);
      try {
        const res = await api.get('/db-monitor/capacity', { params: { periodo: p } });
        setCapacity(res.data || null);
      } catch (e) {
        setCapacityError(e?.response?.data?.message || e?.message || 'Errore caricamento analisi capacità');
      } finally {
        setCapacityLoading(false);
      }
    },
    []
  );

  const fetchLongConnections = useCallback(async () => {
    setLongConnError('');
    setLongConnLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get('/db-monitor/long-connections', { params: { db: 'principale', minSeconds: 60, limit: 50 } }),
        api.get('/db-monitor/long-connections', { params: { db: 'remoto', minSeconds: 60, limit: 50 } })
      ]);
      setLongConn({
        principale: a.data || null,
        remoto: b.data || null
      });
    } catch (e) {
      setLongConnError(e?.response?.data?.message || e?.message || 'Errore caricamento connessioni lunghe');
    } finally {
      setLongConnLoading(false);
    }
  }, []);

  const fetchEmailSendLogs = useCallback(async () => {
    setEmailLogsError('');
    setEmailLogsLoading(true);
    try {
      const res = await api.get('/db-monitor/email-send-logs', {
        params: {
          page: emailLogsPage,
          limit: emailLogsPageSize,
          onlyErrors: emailLogsOnlyErrors ? 1 : 0,
          traceId: emailLogsTraceId || undefined
        }
      });
      setEmailLogsRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setEmailLogsTotal(Number(res.data?.total || 0));
    } catch (e) {
      setEmailLogsError(e?.response?.data?.message || e?.message || 'Errore caricamento log invio email/PEC');
    } finally {
      setEmailLogsLoading(false);
    }
  }, [emailLogsPage, emailLogsOnlyErrors, emailLogsTraceId]);

  const fetchRegistroAttivita = useCallback(async () => {
    setRegistroError('');
    setRegistroLoading(true);
    try {
      const res = await api.get('/db-monitor/registro-attivita', {
        params: {
          page: registroPage,
          limit: registroPageSize,
          search: registroSearch || undefined,
          utenteEmail: registroUtente || undefined,
          funzionalita: registroFunzionalita || undefined,
          operazione: registroOperazione || undefined,
          dataDa: registroDataDa || undefined,
          dataA: registroDataA || undefined,
          sortBy: registroSortBy || undefined,
          sortDir: registroSortDir || undefined
        }
      });
      setRegistroRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setRegistroTotal(Number(res.data?.total || 0));
    } catch (e) {
      setRegistroError(e?.response?.data?.message || e?.message || 'Errore caricamento registro attività');
    } finally {
      setRegistroLoading(false);
    }
  }, [
    registroDataA,
    registroDataDa,
    registroFunzionalita,
    registroOperazione,
    registroUtente,
    registroPage,
    registroSearch,
    registroSortBy,
    registroSortDir
  ]);

  const fetchRegistroFiltri = useCallback(async () => {
    setRegistroFiltriLoading(true);
    try {
      const res = await api.get('/db-monitor/registro-attivita/distinct', { params: { limit: 800 } });
      setRegistroFiltri({
        funzionalita: Array.isArray(res.data?.funzionalita) ? res.data.funzionalita : [],
        operazioni: Array.isArray(res.data?.operazioni) ? res.data.operazioni : [],
        utenti: Array.isArray(res.data?.utenti) ? res.data.utenti : []
      });
    } catch (_) {
      setRegistroFiltri({ funzionalita: [], operazioni: [], utenti: [] });
    } finally {
      setRegistroFiltriLoading(false);
    }
  }, []);

  const fetchRegistroFunzionalitaOptions = useCallback(async (term) => {
    const q = String(term || '').trim();
    try {
      const res = await api.get('/db-monitor/registro-attivita/distinct', {
        params: { only: 'funzionalita', q: q || undefined, limit: 80 }
      });
      const list = Array.isArray(res.data?.funzionalita) ? res.data.funzionalita : [];
      setRegistroFiltri((prev) => ({ ...prev, funzionalita: list }));
    } catch (_) {}
  }, []);

  const pageSize = 10;
  const paginate = (items, page) => {
    const list = Array.isArray(items) ? items : [];
    const p = Math.max(1, Number(page) || 1);
    const start = (p - 1) * pageSize;
    return list.slice(start, start + pageSize);
  };

  const pageCount = (items) => {
    const list = Array.isArray(items) ? items : [];
    return Math.max(1, Math.ceil(list.length / pageSize));
  };

  const doKill = useCallback(async () => {
    if (!killState?.db || !killState?.id) return;
    setKillMessage('');
    setKillLoading(true);
    try {
      await api.post('/db-monitor/kill', {
        db: killState.db,
        id: killState.id,
        mode: killState.mode
      });
      setKillMessage(`Terminato: ${killState.mode === 'query' ? 'query' : 'connessione'} #${killState.id} (${killState.db})`);
      setKillState({ open: false, db: '', id: 0, mode: 'connection' });
      await fetchLongConnections();
    } catch (e) {
      setKillMessage(e?.response?.data?.message || e?.message || 'Errore durante la terminazione');
    } finally {
      setKillLoading(false);
    }
  }, [killState, fetchLongConnections]);

  const doBulkKill = useCallback(async () => {
    const dbKey = String(bulkKillState?.db || '').trim();
    const ids = Array.isArray(bulkKillState?.ids) ? bulkKillState.ids : [];
    const uniq = Array.from(new Set(ids.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0)));
    if (!dbKey || !uniq.length) return;
    setKillMessage('');
    setKillLoading(true);
    try {
      let ok = 0;
      let ko = 0;
      for (const id of uniq) {
        try {
          await api.post('/db-monitor/kill', { db: dbKey, id, mode: 'connection' });
          ok += 1;
        } catch (_) {
          ko += 1;
        }
      }
      setKillMessage(`Terminato: ${ok} connessioni (${dbKey}).${ko ? ` Errori: ${ko}.` : ''}`);
      setBulkKillState({ open: false, db: '', ids: [], view: 'running' });
      await fetchLongConnections();
    } catch (e) {
      setKillMessage(e?.response?.data?.message || e?.message || 'Errore durante la terminazione');
    } finally {
      setKillLoading(false);
    }
  }, [bulkKillState, fetchLongConnections]);

  useEffect(() => {
    fetchLive();
    const id = setInterval(fetchLive, 30_000);
    return () => clearInterval(id);
  }, [fetchLive]);

  useEffect(() => {
    fetchCleanupStatus();
    const id = setInterval(fetchCleanupStatus, 30_000);
    return () => clearInterval(id);
  }, [fetchCleanupStatus]);

  useEffect(() => {
    if (!PERIODI.includes(periodo)) return;
    if (sezione === 'monitor') {
      fetchStorico(periodo);
      fetchSlow(periodo);
    }
    if (sezione === 'capacity') {
      fetchCapacity(periodo);
    }
  }, [periodo, fetchStorico, fetchSlow]);

  useEffect(() => {
    if (!PERIODI.includes(periodo)) return;
    if (sezione === 'capacity') {
      fetchCapacity(periodo);
      fetchLongConnections();
      const id = setInterval(fetchLongConnections, 30_000);
      return () => clearInterval(id);
    }
  }, [sezione, periodo, fetchCapacity, fetchLongConnections]);

  useEffect(() => {
    if (sezione !== 'email_logs') return;
    fetchEmailSendLogs();
  }, [sezione, fetchEmailSendLogs]);

  useEffect(() => {
    if (sezione !== 'registro_attivita') return;
    fetchRegistroFiltri();
    fetchRegistroAttivita();
  }, [sezione, fetchRegistroAttivita, fetchRegistroFiltri]);

  useEffect(() => {
    if (sezione !== 'registro_attivita') return undefined;
    const timeoutId = window.setTimeout(() => {
      fetchRegistroFunzionalitaOptions(registroFunzionalitaInput);
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [sezione, registroFunzionalitaInput, fetchRegistroFunzionalitaOptions]);

  const storicoMerged = useMemo(() => {
    const map = new Map();
    for (const row of storico.principale || []) {
      const bucket = row.bucket;
      if (!bucket) continue;
      map.set(bucket, {
        bucket,
        label: formatBucket(bucket, periodo),
        conn_principale: safeNumber(row.connessioni_attive),
        qps_principale: safeNumber(row.query_al_secondo),
        lat_principale: safeNumber(row.latenza_ms),
        slow_principale: safeNumber(row.query_lente_delta),
        pool_principale: safeNumber(row.pool_dimensione)
      });
    }
    for (const row of storico.remoto || []) {
      const bucket = row.bucket;
      if (!bucket) continue;
      const base = map.get(bucket) || { bucket, label: formatBucket(bucket, periodo) };
      map.set(bucket, {
        ...base,
        conn_remoto: safeNumber(row.connessioni_attive),
        qps_remoto: safeNumber(row.query_al_secondo),
        lat_remoto: safeNumber(row.latenza_ms),
        slow_remoto: safeNumber(row.query_lente_delta),
        pool_remoto: safeNumber(row.pool_dimensione)
      });
    }
    return Array.from(map.values()).sort((a, b) => String(a.bucket).localeCompare(String(b.bucket)));
  }, [storico.principale, storico.remoto, periodo]);

  const slowMerged = useMemo(() => {
    const map = new Map();
    for (const row of slow.principale || []) {
      const bucket = row.bucket;
      if (!bucket) continue;
      map.set(bucket, {
        bucket,
        label: formatBucket(bucket, periodo),
        slow_principale: safeNumber(row.query_lente_delta_totale)
      });
    }
    for (const row of slow.remoto || []) {
      const bucket = row.bucket;
      if (!bucket) continue;
      const base = map.get(bucket) || { bucket, label: formatBucket(bucket, periodo) };
      map.set(bucket, {
        ...base,
        slow_remoto: safeNumber(row.query_lente_delta_totale)
      });
    }
    return Array.from(map.values()).sort((a, b) => {
      const at = safeNumber(a.slow_principale) + safeNumber(a.slow_remoto);
      const bt = safeNumber(b.slow_principale) + safeNumber(b.slow_remoto);
      return bt - at;
    });
  }, [slow.principale, slow.remoto, periodo]);

  const kpi = useMemo(() => {
    const rows = storicoMerged;
    if (!rows.length) {
      return {
        avgConnPrincipale: 0,
        avgConnRemoto: 0,
        avgQps: 0,
        totalSlow: 0,
        peakConn: 0,
        variationPct: 0,
        hasData: false
      };
    }

    let sumConnP = 0;
    let sumConnR = 0;
    let sumQps = 0;
    let sumSlow = 0;
    let peak = 0;

    for (const r of rows) {
      const cp = safeNumber(r.conn_principale);
      const cr = safeNumber(r.conn_remoto);
      const qp = safeNumber(r.qps_principale);
      const qr = safeNumber(r.qps_remoto);
      const sp = safeNumber(r.slow_principale);
      const sr = safeNumber(r.slow_remoto);

      sumConnP += cp;
      sumConnR += cr;
      sumQps += (qp + qr) / 2;
      sumSlow += sp + sr;
      peak = Math.max(peak, cp, cr);
    }

    const mid = Math.floor(rows.length / 2);
    const first = rows.slice(0, mid);
    const last = rows.slice(mid);

    const avgTotal = (arr) => {
      if (!arr.length) return 0;
      const total = arr.reduce((acc, r) => acc + safeNumber(r.conn_principale) + safeNumber(r.conn_remoto), 0);
      return total / arr.length;
    };

    const firstAvg = avgTotal(first);
    const lastAvg = avgTotal(last);
    const variationPct = firstAvg > 0 ? ((lastAvg - firstAvg) / firstAvg) * 100 : 0;

    return {
      avgConnPrincipale: sumConnP / rows.length,
      avgConnRemoto: sumConnR / rows.length,
      avgQps: sumQps / rows.length,
      totalSlow: sumSlow,
      peakConn: peak,
      variationPct,
      hasData: true
    };
  }, [storicoMerged]);

  const showLoading = liveLoading || storicoLoading || slowLoading;
  const showError = liveError || storicoError || slowError;

  const openHelp = useCallback((title, content) => {
    setHelpState({ open: true, title, content });
  }, []);

  const cleanupIndicator = useMemo(() => {
    const s = cleanupStatus;
    if (!s) {
      return {
        label: 'Auto-cleanup: -',
        color: 'default',
        tooltip:
          'Stato non disponibile.\n\nPossibili cause:\n- non sei loggato come Admin (endpoint protetto)\n- backend non raggiungibile\n- errore temporaneo di rete'
      };
    }
    const enabled = Boolean(s.enabled);
    const inWindow = Boolean(s.inWindow);
    const state = String(s.state || '');
    const lastOk = s.lastSuccessAt ? formatItalyDateTime(parseMysqlDatetimeUtc(s.lastSuccessAt), true) : '';
    const lastFail = s.lastFailAt ? formatItalyDateTime(parseMysqlDatetimeUtc(s.lastFailAt), true) : '';
    const minSeconds = Number(s.minSeconds || 0);

    if (!enabled) {
      return {
        label: 'Auto-cleanup: OFF',
        color: 'default',
        tooltip: `Disattivato (imposta DB_CONN_CLEANUP_ENABLED=1 nel .env root del docker-compose).\n\nFinestra: ${s?.window?.start || '08:30'}–${s?.window?.end || '19:00'} (${s?.tz || 'Europe/Rome'}).`
      };
    }
    if (!inWindow) {
      return {
        label: 'Auto-cleanup: Pausa',
        color: 'info',
        tooltip: `Attivo ma fuori fascia.\n\nFinestra: ${s?.window?.start || '08:30'}–${s?.window?.end || '19:00'} (${s?.tz || 'Europe/Rome'}).\nSoglia: > ${Math.round(minSeconds)}s.\nUltimo OK: ${lastOk || '-'}.`
      };
    }
    if (state === 'ok') {
      return {
        label: 'Auto-cleanup: OK',
        color: 'success',
        tooltip: `In esecuzione nella fascia.\n\nSoglia: > ${Math.round(minSeconds)}s.\nUltimo OK: ${lastOk || '-'}.`
      };
    }
    if (state === 'error') {
      return {
        label: 'Auto-cleanup: Errore',
        color: 'error',
        tooltip: `Errore recente.\n\nUltimo OK: ${lastOk || '-'}.\nUltimo errore: ${lastFail || '-'}.`
      };
    }
    if (state === 'stale') {
      return {
        label: 'Auto-cleanup: KO',
        color: 'warning',
        tooltip: `Nessun OK recente nella fascia.\n\nUltimo OK: ${lastOk || '-'}.\nUltimo errore: ${lastFail || '-'}.`
      };
    }
    return {
      label: 'Auto-cleanup: -',
      color: 'warning',
      tooltip: `Stato: ${state || 'n/d'}.\n\nUltimo OK: ${lastOk || '-'}.\nUltimo errore: ${lastFail || '-'}.`
    };
  }, [cleanupStatus]);

  const caricoOrarioRemoto = useMemo(() => {
    if (periodo !== 'giornaliero') return [];
    return (storicoMerged || []).map((r) => {
      const pool = Math.max(safeNumber(r.pool_remoto), 0);
      const used = Math.max(safeNumber(r.conn_remoto), 0);
      const pct = pool > 0 ? Math.min(Math.max((used / pool) * 100, 0), 100) : 0;
      return {
        label: r.label,
        carico: pct,
        slow: safeNumber(r.slow_remoto)
      };
    });
  }, [storicoMerged, periodo]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              color: '#4f46e5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <DatabaseIcon size={20} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
              Monitoraggio DB
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitoraggio performance DB
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <MuiTooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{cleanupIndicator.tooltip}</span>}>
            <Chip
              size="small"
              color={cleanupIndicator.color}
              variant="outlined"
              label={cleanupIndicator.label}
              onClick={() => openHelp('Auto-cleanup connessioni DB', cleanupIndicator.tooltip)}
              sx={{ borderRadius: '16px', fontWeight: 800 }}
            />
          </MuiTooltip>
          <Tabs value={periodo} onChange={(_e, v) => setPeriodo(v)} sx={{ minHeight: 36 }}>
            <Tab value="giornaliero" label="Giornaliero" />
            <Tab value="settimanale" label="Settimanale" />
            <Tab value="mensile" label="Mensile" />
          </Tabs>
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Tabs value={sezione} onChange={(_e, v) => setSezione(v)} sx={{ minHeight: 36 }}>
          <Tab value="monitor" label="Monitoraggio" />
          <Tab value="capacity" label="Analisi capacità" />
          <Tab value="email_logs" label="Log Email/PEC" />
          <Tab value="registro_attivita" label="Registro attività" />
        </Tabs>
      </Box>

      <Dialog open={helpState.open} onClose={() => setHelpState({ open: false, title: '', content: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{helpState.title}</DialogTitle>
        <DialogContent dividers>
          {typeof helpState.content === 'string' ? (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
              {helpState.content}
            </Typography>
          ) : (
            helpState.content
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpState({ open: false, title: '', content: null })}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={killState.open} onClose={() => (killLoading ? null : setKillState({ open: false, db: '', id: 0, mode: 'connection' }))} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {`Vuoi terminare ${killState.mode === 'query' ? 'la query' : 'la connessione'} #${killState.id} su DB ${killState.db}?\n\nQuesta operazione può interrompere operazioni in corso.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKillState({ open: false, db: '', id: 0, mode: 'connection' })} disabled={killLoading}>
            Annulla
          </Button>
          <Button onClick={doKill} disabled={killLoading} color="error" variant="contained">
            Termina
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkKillState.open}
        onClose={() => (killLoading ? null : setBulkKillState({ open: false, db: '', ids: [], view: 'running' }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Conferma</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {`Vuoi terminare tutte le connessioni > 10 minuti (${Array.isArray(bulkKillState.ids) ? bulkKillState.ids.length : 0}) su DB ${
              bulkKillState.db
            }?\n\nQuesta operazione può interrompere operazioni in corso.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkKillState({ open: false, db: '', ids: [], view: 'running' })} disabled={killLoading}>
            Annulla
          </Button>
          <Button onClick={doBulkKill} disabled={killLoading} color="error" variant="contained">
            Termina tutte
          </Button>
        </DialogActions>
      </Dialog>

      {sezione === 'monitor' && showError ? (
        <Card variant="outlined" sx={{ ...CARD_ELEVATION_SX, p: 2, borderRadius: 2, mb: 2 }}>
          <Typography variant="body2" color="error">
            {showError}
          </Typography>
        </Card>
      ) : null}

      {sezione === 'capacity' && capacityError ? (
        <Card variant="outlined" sx={{ ...CARD_ELEVATION_SX, p: 2, borderRadius: 2, mb: 2 }}>
          <Typography variant="body2" color="error">
            {capacityError}
          </Typography>
        </Card>
      ) : null}

      {sezione === 'email_logs' && emailLogsError ? (
        <Card variant="outlined" sx={{ ...CARD_ELEVATION_SX, p: 2, borderRadius: 2, mb: 2 }}>
          <Typography variant="body2" color="error">
            {emailLogsError}
          </Typography>
        </Card>
      ) : null}

      {sezione === 'registro_attivita' && registroError ? (
        <Card variant="outlined" sx={{ ...CARD_ELEVATION_SX, p: 2, borderRadius: 2, mb: 2 }}>
          <Typography variant="body2" color="error">
            {registroError}
          </Typography>
        </Card>
      ) : null}

      {sezione === 'email_logs' ? (
        <Card variant="outlined" sx={{ ...CARD_ELEVATION_SX, p: 2, borderRadius: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
            <TextField
              size="small"
              label="Trace ID"
              value={emailLogsTraceIdInput}
              onChange={(e) => setEmailLogsTraceIdInput(e.target.value)}
              sx={{ minWidth: 320 }}
            />
            <Button
              variant="contained"
              onClick={() => {
                setEmailLogsTraceId(String(emailLogsTraceIdInput || '').trim());
                setEmailLogsPage(1);
              }}
              disabled={emailLogsLoading}
            >
              Cerca
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setEmailLogsTraceIdInput('');
                setEmailLogsTraceId('');
                setEmailLogsPage(1);
              }}
              disabled={emailLogsLoading}
            >
              Reset
            </Button>
            <FormControlLabel
              control={
                <Switch
                  checked={emailLogsOnlyErrors}
                  onChange={(_e, checked) => {
                    setEmailLogsOnlyErrors(checked);
                    setEmailLogsPage(1);
                  }}
                />
              }
              label="Solo errori"
            />
            <Button variant="text" onClick={fetchEmailSendLogs} disabled={emailLogsLoading}>
              Aggiorna
            </Button>
            <Chip size="small" color="primary" variant="outlined" label={`Totale: ${emailLogsTotal}`} />
          </Box>

          {emailLogsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : emailLogsRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nessun log trovato.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data/Ora</TableCell>
                    <TableCell>Esito</TableCell>
                    <TableCell>Codice</TableCell>
                    <TableCell>HTTP</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Trace ID</TableCell>
                    <TableCell>Errore</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emailLogsRows.map((r) => {
                    const errorText = r?.error_message ? String(r.error_message) : '';
                    const shortError = errorText && errorText.length > 140 ? `${errorText.slice(0, 137)}...` : errorText;
                    const esito = String(r?.esito || '').toLowerCase();
                    const esitoColor = esito === 'ok' ? 'success' : esito === 'errore' ? 'error' : 'default';
                    return (
                      <TableRow
                        key={String(r?.id)}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          let details = null;
                          try {
                            details = r?.details_json ? JSON.parse(String(r.details_json)) : null;
                          } catch {
                            details = r?.details_json ? String(r.details_json) : null;
                          }
                          openHelp(
                            `Log invio email/PEC — ${r?.trace_id || ''}`,
                            JSON.stringify(
                              {
                                id: r?.id,
                                creato_il: r?.creato_il,
                                esito: r?.esito,
                                code: r?.code,
                                http_status: r?.http_status,
                                account_id: r?.account_id,
                                account_email: r?.account_email,
                                operatore_user_id: r?.operatore_user_id,
                                smtp_message_id: r?.smtp_message_id,
                                error_message: r?.error_message,
                                details
                              },
                              null,
                              2
                            )
                          );
                        }}
                      >
                        <TableCell>{r?.creato_il || ''}</TableCell>
                        <TableCell>
                          <Chip size="small" label={r?.esito || ''} color={esitoColor} />
                        </TableCell>
                        <TableCell>{r?.code || ''}</TableCell>
                        <TableCell>{r?.http_status != null ? String(r.http_status) : ''}</TableCell>
                        <TableCell>{r?.account_email || (r?.account_id != null ? `#${r.account_id}` : '')}</TableCell>
                        <TableCell>{r?.operatore_user_id != null ? String(r.operatore_user_id) : ''}</TableCell>
                        <TableCell>{r?.trace_id || ''}</TableCell>
                        <TableCell>{shortError}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Pagination
              count={Math.max(1, Math.ceil(emailLogsTotal / emailLogsPageSize))}
              page={emailLogsPage}
              onChange={(_e, p) => setEmailLogsPage(p)}
              color="primary"
              shape="rounded"
            />
          </Box>
        </Card>
      ) : null}

      {sezione === 'registro_attivita' ? (
        <Card variant="outlined" sx={{ ...CARD_ELEVATION_SX, p: 2, borderRadius: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
            <TextField
              size="small"
              label="Cerca"
              value={registroSearchInput}
              onChange={(e) => setRegistroSearchInput(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <Autocomplete
              size="small"
              options={registroFiltri.funzionalita || []}
              value={registroFunzionalitaInput || ''}
              inputValue={registroFunzionalitaInput || ''}
              onInputChange={(_e, v) => setRegistroFunzionalitaInput(v)}
              onChange={(_e, v) => setRegistroFunzionalitaInput(v || '')}
              freeSolo
              disabled={registroFiltriLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Funzionalità"
                  placeholder="Tutte"
                  sx={{ minWidth: 260 }}
                />
              )}
            />
            <Autocomplete
              size="small"
              options={registroFiltri.operazioni || []}
              value={registroOperazioneInput || ''}
              inputValue={registroOperazioneInput || ''}
              onInputChange={(_e, v) => setRegistroOperazioneInput(v)}
              onChange={(_e, v) => setRegistroOperazioneInput(v || '')}
              freeSolo
              disabled={registroFiltriLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Operazione"
                  placeholder="Tutte"
                  sx={{ minWidth: 200 }}
                />
              )}
            />
            <Autocomplete
              size="small"
              options={registroFiltri.utenti || []}
              value={registroUtenteInput || ''}
              inputValue={registroUtenteInput || ''}
              onInputChange={(_e, v) => setRegistroUtenteInput(v)}
              onChange={(_e, v) => setRegistroUtenteInput(v || '')}
              freeSolo
              disabled={registroFiltriLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Utente"
                  placeholder="Tutti"
                  sx={{ minWidth: 260 }}
                />
              )}
            />
            <TextField
              size="small"
              label="Da"
              type="date"
              value={registroDataDaInput}
              onChange={(e) => setRegistroDataDaInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="A"
              type="date"
              value={registroDataAInput}
              onChange={(e) => setRegistroDataAInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              onClick={() => {
                setRegistroSearch(String(registroSearchInput || '').trim());
                setRegistroFunzionalita(String(registroFunzionalitaInput || '').trim());
                setRegistroOperazione(String(registroOperazioneInput || '').trim());
                setRegistroUtente(String(registroUtenteInput || '').trim());
                setRegistroDataDa(String(registroDataDaInput || '').trim());
                setRegistroDataA(String(registroDataAInput || '').trim());
                setRegistroPage(1);
              }}
              disabled={registroLoading}
            >
              Cerca
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setRegistroSearchInput('');
                setRegistroSearch('');
                setRegistroFunzionalitaInput('');
                setRegistroFunzionalita('');
                setRegistroOperazioneInput('');
                setRegistroOperazione('');
                setRegistroUtenteInput('');
                setRegistroUtente('');
                setRegistroDataDaInput('');
                setRegistroDataDa('');
                setRegistroDataAInput('');
                setRegistroDataA('');
                setRegistroSortBy('creato_il');
                setRegistroSortDir('DESC');
                setRegistroPage(1);
              }}
              disabled={registroLoading}
            >
              Reset
            </Button>
            <Button variant="text" onClick={fetchRegistroAttivita} disabled={registroLoading}>
              Aggiorna
            </Button>
            <Chip size="small" color="primary" variant="outlined" label={`Totale: ${registroTotal}`} />
          </Box>

          {registroLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : registroRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nessuna attività trovata.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      { key: 'creato_il', label: 'Data/Ora' },
                      { key: 'utente_email', label: 'Utente' },
                      { key: 'ruolo', label: 'Ruolo' },
                      { key: 'funzionalita', label: 'Funzionalità' },
                      { key: 'operazione', label: 'Operazione' },
                      { key: 'percorso', label: 'Percorso' },
                      { key: 'entita_tipo', label: 'Entità' },
                      { key: 'ip', label: 'IP' }
                    ].map((c) => (
                      <TableCell
                        key={c.key}
                        sortDirection={registroSortBy === c.key ? registroSortDir.toLowerCase() : false}
                      >
                        <TableSortLabel
                          active={registroSortBy === c.key}
                          direction={registroSortBy === c.key ? registroSortDir.toLowerCase() : 'asc'}
                          onClick={() => {
                            const prevDir = String(registroSortDir || '').toUpperCase();
                            const nextDir = registroSortBy === c.key && prevDir === 'ASC' ? 'DESC' : 'ASC';
                            setRegistroSortBy(c.key);
                            setRegistroSortDir(nextDir);
                            setRegistroPage(1);
                          }}
                        >
                          {c.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {registroRows.map((r) => {
                    const entita = r?.entita_tipo ? `${r.entita_tipo}${r?.entita_id ? `#${r.entita_id}` : ''}` : '';
                    return (
                      <TableRow
                        key={String(r?.id)}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          let meta = null;
                          try {
                            meta = r?.meta_json != null ? r.meta_json : null;
                            if (typeof meta === 'string') meta = JSON.parse(meta);
                          } catch {
                            meta = r?.meta_json != null ? String(r.meta_json) : null;
                          }
                          const payload = {
                            id: r?.id,
                            creato_il: r?.creato_il,
                            utente_id: r?.utente_id,
                            utente_email: r?.utente_email,
                            ruolo: r?.ruolo,
                            dipartimento_nome: r?.dipartimento_nome,
                            funzionalita: r?.funzionalita,
                            operazione: r?.operazione,
                            percorso: r?.percorso,
                            entita_tipo: r?.entita_tipo,
                            entita_id: r?.entita_id,
                            ip: r?.ip,
                            user_agent: r?.user_agent,
                            meta_json: meta
                          };
                          openHelp(`Registro attività — #${r?.id || ''}`, JSON.stringify(payload, null, 2));
                        }}
                      >
                        <TableCell>{formatItalyDateTime(parseMysqlDatetimeUtc(r?.creato_il), true) || (r?.creato_il || '')}</TableCell>
                        <TableCell>{r?.utente_email || ''}</TableCell>
                        <TableCell>{r?.ruolo || ''}</TableCell>
                        <TableCell>{r?.funzionalita || ''}</TableCell>
                        <TableCell>{r?.operazione || ''}</TableCell>
                        <TableCell>{r?.percorso || ''}</TableCell>
                        <TableCell>{entita}</TableCell>
                        <TableCell>{r?.ip || ''}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Pagination
              count={Math.max(1, Math.ceil(registroTotal / registroPageSize))}
              page={registroPage}
              onChange={(_e, p) => setRegistroPage(p)}
              color="primary"
              shape="rounded"
            />
          </Box>
        </Card>
      ) : null}

      {sezione === 'monitor' ? (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard
            title="Conn. media DB1"
            value={`${kpi.avgConnPrincipale.toFixed(1)} conn`}
            subtitle="DB Principale"
            accentColor="#1976d2"
            loading={!kpi.hasData}
            onHelp={() =>
              openHelp(
                'Conn. media DB1',
                'Media delle connessioni attive del DB Principale nel periodo selezionato.\nFonte: Threads_connected (MySQL).'
              )
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard
            title="Conn. media DB2"
            value={`${kpi.avgConnRemoto.toFixed(1)} conn`}
            subtitle="DB Remoto"
            accentColor="#2e7d32"
            loading={!kpi.hasData}
            onHelp={() =>
              openHelp(
                'Conn. media DB2',
                'Media delle connessioni attive del DB Remoto nel periodo selezionato.\nFonte: Threads_connected (MySQL).'
              )
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard
            title="Slow queries tot."
            value={`${Math.round(kpi.totalSlow)}`}
            subtitle="Somma nel periodo"
            accentColor="#ed6c02"
            loading={!kpi.hasData}
            onHelp={() =>
              openHelp(
                'Slow queries tot.',
                'Somma di query_lente_delta nel periodo selezionato.\nquery_lente_delta = incremento di Slow_queries tra snapshot.'
              )
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard
            title="Query/s media"
            value={`${kpi.avgQps.toFixed(2)} qps`}
            subtitle="Media nel periodo"
            accentColor="#9c27b0"
            loading={!kpi.hasData}
            onHelp={() =>
              openHelp(
                'Query/s media',
                'Media del valore QPS nel periodo selezionato.\nQPS = domande_delta / secondi tra ultimo e penultimo snapshot.\nFonte: Questions (MySQL).'
              )
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard
            title="Picco connessioni"
            value={`${Math.round(kpi.peakConn)} max`}
            subtitle="Massimo osservato"
            accentColor="#7b1fa2"
            loading={!kpi.hasData}
            onHelp={() =>
              openHelp(
                'Picco connessioni',
                'Massimo di connessioni attive osservato (tra DB Principale e DB Remoto) nel periodo selezionato.'
              )
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard
            title="Variazione %"
            value={`${kpi.variationPct >= 0 ? '+' : ''}${kpi.variationPct.toFixed(1)}%`}
            subtitle="2ª metà vs 1ª metà"
            accentColor={kpi.variationPct >= 0 ? '#2e7d32' : '#d32f2f'}
            loading={!kpi.hasData}
            onHelp={() =>
              openHelp(
                'Variazione %',
                'Confronto tra 2ª metà e 1ª metà del periodo selezionato.\nCalcolata sulla media della somma connessioni (DB1 + DB2).'
              )
            }
          />
        </Grid>
      </Grid>
      ) : null}

      {sezione === 'monitor' ? (
        <>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
            Live
          </Typography>

          {showLoading && !live.principale && !live.remoto ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ position: 'relative' }}>
                  <Box sx={{ position: 'absolute', top: 10, right: 56, zIndex: 1 }}>
                    <MuiTooltip title="Info">
                      <IconButton
                        size="small"
                        onClick={() =>
                          openHelp(
                            'Live — DB Principale',
                            'Dati dell’ultimo snapshot.\n\n- Connessioni attive: Threads_connected\n- QPS: domande_delta / secondi tra snapshot\n- Latenza (ms): round-trip di SELECT 1\n- Slow queries Δ: incremento Slow_queries tra snapshot\n- Pool: connessioni attive / pool_dimensione'
                          )
                        }
                      >
                        <HelpCircle size={16} />
                      </IconButton>
                    </MuiTooltip>
                  </Box>
                  <MetricCard title="DB Principale" snapshot={live.principale} />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ position: 'relative' }}>
                  <Box sx={{ position: 'absolute', top: 10, right: 56, zIndex: 1 }}>
                    <MuiTooltip title="Info">
                      <IconButton
                        size="small"
                        onClick={() =>
                          openHelp(
                            'Live — DB Remoto',
                            'Dati dell’ultimo snapshot.\n\n- Connessioni attive: Threads_connected\n- QPS: domande_delta / secondi tra snapshot\n- Latenza (ms): round-trip di SELECT 1\n- Slow queries Δ: incremento Slow_queries tra snapshot\n- Pool: connessioni attive / pool_dimensione'
                          )
                        }
                      >
                        <HelpCircle size={16} />
                      </IconButton>
                    </MuiTooltip>
                  </Box>
                  <MetricCard title="DB Remoto" snapshot={live.remoto} />
                </Box>
              </Grid>
            </Grid>
          )}

          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
            Storico
          </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <ChartCard
            title="Connessioni attive nel tempo"
            onHelp={() =>
              openHelp(
                'Connessioni attive nel tempo',
                'Andamento delle connessioni attive nel periodo selezionato.\nValore: media (AVG) per bucket temporale.\nFonte: Threads_connected.'
              )
            }
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={storicoMerged}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="conn_principale" name="DB Principale" stroke="#1976d2" fill="#1976d220" />
                <Area type="monotone" dataKey="conn_remoto" name="DB Remoto" stroke="#2e7d32" fill="#2e7d3220" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ ...CARD_ELEVATION_SX, p: 2, borderRadius: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
                Carico per ora del giorno
              </Typography>
              <MuiTooltip title="Info">
                <IconButton
                  size="small"
                  onClick={() =>
                    openHelp(
                      'Carico per ora del giorno',
                      'Disponibile nella vista Giornaliero.\nStima del carico del DB Remoto: connessioni_attive / pool_dimensione * 100.\npool_dimensione è il connectionLimit del pool backend.'
                    )
                  }
                >
                  <HelpCircle size={16} />
                </IconButton>
              </MuiTooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              % utilizzo medio · DB Remoto
            </Typography>
            <Box sx={{ width: '100%', height: 152, mb: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={caricoOrarioRemoto}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="carico" name="Carico %" fill="#2e7d32" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Slow queries per periodo
              </Typography>
              <MuiTooltip title="Info">
                <IconButton
                  size="small"
                  onClick={() =>
                    openHelp(
                      'Slow queries per periodo',
                      'Grafico di query_lente_delta (incremento Slow_queries tra snapshot).\nValori aggregati per bucket temporale.'
                    )
                  }
                >
                  <HelpCircle size={16} />
                </IconButton>
              </MuiTooltip>
            </Box>
            <Box sx={{ width: '100%', height: 120 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={storicoMerged}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="slow_remoto" name="DB Remoto" fill="#ed6c02" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <ChartCard
            title="Query per secondo"
            onHelp={() =>
              openHelp(
                'Query per secondo',
                'Andamento del QPS nel periodo selezionato.\nQPS = domande_delta / secondi tra snapshot.\nFonte: Questions (MySQL).'
              )
            }
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={storicoMerged}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="qps_principale" name="DB Principale" stroke="#1976d2" dot={false} />
                <Line type="monotone" dataKey="qps_remoto" name="DB Remoto" stroke="#2e7d32" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard
            title="Latenza ping (ms)"
            onHelp={() =>
              openHelp(
                'Latenza ping (ms)',
                'Tempo di risposta misurato con una SELECT 1.\nValori aggregati per bucket temporale.'
              )
            }
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={storicoMerged}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="lat_principale" name="DB Principale" stroke="#1976d2" fill="#1976d220" />
                <Area type="monotone" dataKey="lat_remoto" name="DB Remoto" stroke="#2e7d32" fill="#2e7d3220" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard
            title="Slow queries Δ"
            onHelp={() =>
              openHelp(
                'Slow queries Δ',
                'Incremento di Slow_queries tra snapshot.\nValori aggregati per bucket temporale.'
              )
            }
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={storicoMerged}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="slow_principale" name="DB Principale" fill="#1976d2" />
                <Bar dataKey="slow_remoto" name="DB Remoto" fill="#2e7d32" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
            Slow queries (top finestre)
          </Typography>

          <Card variant="outlined" sx={{ ...CARD_ELEVATION_SX, p: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Top 10 finestre con maggior incremento slow queries
          </Typography>
          <MuiTooltip title="Info">
            <IconButton
              size="small"
              onClick={() =>
                openHelp(
                  'Slow queries (top finestre)',
                  'Top 10 finestre temporali con maggiore somma di query_lente_delta.\nÈ un’aggregazione su bucket: non contiene il testo delle query.'
                )
              }
            >
              <HelpCircle size={16} />
            </IconButton>
          </MuiTooltip>
        </Box>
        <Box sx={{ width: '100%', height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={slowMerged}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={0} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="slow_principale" name="Principale" fill="#1976d2" />
              <Bar dataKey="slow_remoto" name="Remoto" fill="#2e7d32" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
          </Card>
        </>
      ) : null}

      {sezione === 'capacity' ? (
        <Box>
          {capacityLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ ...CARD_ELEVATION_SX, p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      DB Principale
                    </Typography>
                    <MuiTooltip title="Info">
                      <IconButton
                        size="small"
                        onClick={() =>
                          openHelp(
                            'Analisi capacità — DB Principale',
                            'Statistiche calcolate dagli snapshot in db_metriche nel periodo selezionato.\n\nP95: valore sotto cui cade il 95% dei campioni.'
                          )
                        }
                      >
                        <HelpCircle size={16} />
                      </IconButton>
                    </MuiTooltip>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {(() => {
                      const from = capacity?.principal?.stats?.range?.from || '';
                      const to = capacity?.principal?.stats?.range?.to || '';
                      if (!from || !to) return 'Rilevazione: -';
                      const fromIt = formatItalyDateTime(parseMysqlDatetimeUtc(from), true);
                      const toIt = formatItalyDateTime(parseMysqlDatetimeUtc(to), true);
                      return `Rilevazione: dal ${fromIt} al ${toIt}`;
                    })()}
                  </Typography>

                  <Box sx={{ display: 'grid', gap: 0.75 }}>
                    <Stat label="Campioni" value={capacity?.principal?.stats?.samples ?? '-'} icon={BarChart3} iconColor="#4f46e5" />
                    <Stat label="Errori" value={capacity?.principal?.stats?.errors ?? '-'} icon={AlertTriangle} iconColor="#d32f2f" />
                    <Stat label="max_connections" value={capacity?.principal?.variables?.max_connections ?? '-'} icon={PlugZap} iconColor="#7b1fa2" />
                    <Stat label="Max_used_connections (MySQL)" value={capacity?.principal?.stats?.max_used_connections ?? '-'} icon={Users} iconColor="#1976d2" />
                    <Stat label="Threads_connected P95" value={Number(capacity?.principal?.stats?.threads_connected?.p95 ?? 0).toFixed(0)} icon={Users} iconColor="#1976d2" />
                    <Stat label="Threads_connected Max" value={capacity?.principal?.stats?.threads_connected?.max ?? '-'} icon={Users} iconColor="#1976d2" />
                    <Stat label="Threads_running P95" value={Number(capacity?.principal?.stats?.threads_running?.p95 ?? 0).toFixed(0)} icon={Activity} iconColor="#2e7d32" />
                    <Stat label="QPS P95" value={Number(capacity?.principal?.stats?.qps?.p95 ?? 0).toFixed(2)} icon={Gauge} iconColor="#9c27b0" />
                    <Stat label="Latenza P95 (ms)" value={Number(capacity?.principal?.stats?.latency_ms?.p95 ?? 0).toFixed(0)} icon={Timer} iconColor="#ed6c02" />
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ ...CARD_ELEVATION_SX, p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      DB Remoto
                    </Typography>
                    <MuiTooltip title="Info">
                      <IconButton
                        size="small"
                        onClick={() =>
                          openHelp(
                            'Analisi capacità — DB Remoto',
                            'Statistiche calcolate dagli snapshot in db_metriche nel periodo selezionato.\n\nP95: valore sotto cui cade il 95% dei campioni.'
                          )
                        }
                      >
                        <HelpCircle size={16} />
                      </IconButton>
                    </MuiTooltip>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {(() => {
                      const from = capacity?.remote?.stats?.range?.from || '';
                      const to = capacity?.remote?.stats?.range?.to || '';
                      if (!from || !to) return 'Rilevazione: -';
                      const fromIt = formatItalyDateTime(parseMysqlDatetimeUtc(from), true);
                      const toIt = formatItalyDateTime(parseMysqlDatetimeUtc(to), true);
                      return `Rilevazione: dal ${fromIt} al ${toIt}`;
                    })()}
                  </Typography>

                  <Box sx={{ display: 'grid', gap: 0.75 }}>
                    <Stat label="Campioni" value={capacity?.remote?.stats?.samples ?? '-'} icon={BarChart3} iconColor="#4f46e5" />
                    <Stat label="Errori" value={capacity?.remote?.stats?.errors ?? '-'} icon={AlertTriangle} iconColor="#d32f2f" />
                    <Stat label="max_connections" value={capacity?.remote?.variables?.max_connections ?? '-'} icon={PlugZap} iconColor="#7b1fa2" />
                    <Stat label="Max_used_connections (MySQL)" value={capacity?.remote?.stats?.max_used_connections ?? '-'} icon={Users} iconColor="#2e7d32" />
                    <Stat label="Threads_connected P95" value={Number(capacity?.remote?.stats?.threads_connected?.p95 ?? 0).toFixed(0)} icon={Users} iconColor="#2e7d32" />
                    <Stat label="Threads_connected Max" value={capacity?.remote?.stats?.threads_connected?.max ?? '-'} icon={Users} iconColor="#2e7d32" />
                    <Stat label="Threads_running P95" value={Number(capacity?.remote?.stats?.threads_running?.p95 ?? 0).toFixed(0)} icon={Activity} iconColor="#2e7d32" />
                    <Stat label="QPS P95" value={Number(capacity?.remote?.stats?.qps?.p95 ?? 0).toFixed(2)} icon={Gauge} iconColor="#9c27b0" />
                    <Stat label="Latenza P95 (ms)" value={Number(capacity?.remote?.stats?.latency_ms?.p95 ?? 0).toFixed(0)} icon={Timer} iconColor="#ed6c02" />
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ ...CARD_ELEVATION_SX, p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      Connessioni lunghe / bloccate
                    </Typography>
                    <MuiTooltip title="Info">
                      <IconButton
                        size="small"
                        onClick={() =>
                          openHelp(
                            'Connessioni lunghe / bloccate',
                            'Lista delle connessioni MySQL con TIME >= 60s (INFORMATION_SCHEMA.PROCESSLIST).\n\nTop running: connessioni non in Sleep (spesso query in esecuzione o in attesa).\nTop sleeping: connessioni in Sleep tenute aperte.\n\nTIME è il tempo (in secondi) da quanto la connessione è in quello stato.'
                          )
                        }
                      >
                        <HelpCircle size={16} />
                      </IconButton>
                    </MuiTooltip>
                  </Box>

                  {longConnError ? (
                    <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                      {longConnError}
                    </Typography>
                  ) : null}
                  {killMessage ? (
                    <Typography
                      variant="body2"
                      color={killMessage.toLowerCase().includes('errore') ? 'error' : 'text.secondary'}
                      sx={{ mb: 1 }}
                    >
                      {killMessage}
                    </Typography>
                  ) : null}

                  {longConnLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={22} />
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {[
                        { key: 'principale', title: 'DB Principale', color: '#1976d2' },
                        { key: 'remoto', title: 'DB Remoto', color: '#2e7d32' }
                      ].map((cfg) => {
                        const data = cfg.key === 'principale' ? longConn?.principale : longConn?.remoto;
                        const maxTime = safeNumber(data?.maxTimeSeconds);
                        const topRunning = Array.isArray(data?.topRunning) ? data.topRunning : [];
                        const topSleeping = Array.isArray(data?.topSleeping) ? data.topSleeping : [];
                        const view = longConnView?.[cfg.key] === 'sleeping' ? 'sleeping' : 'running';
                        const items = view === 'running' ? topRunning : topSleeping;
                        const bulkIds = Array.from(
                          new Set(
                            (items || [])
                              .filter((r) => safeNumber(r?.time_seconds) >= 600)
                              .map((r) => Number(r?.id))
                              .filter((v) => Number.isFinite(v) && v > 0)
                          )
                        );
                        const pages = pageCount(items);
                        const page = Math.min(longConnPages?.[cfg.key]?.[view] || 1, pages);
                        const slice = paginate(items, page);

                        return (
                          <Grid item xs={12} md={6} key={cfg.key}>
                            <Box sx={{ border: '1px solid rgba(145, 158, 171, 0.24)', borderRadius: 2, p: 1.5 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 0.5 }}>
                                {cfg.title}
                              </Typography>

                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {maxTime > 0
                                  ? `Connessioni aperte da ${Math.round(maxTime)}s = ${formatDurationSeconds(maxTime)}`
                                  : 'Nessuna connessione >= 60s'}
                              </Typography>

                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                <Tabs
                                  value={view}
                                  onChange={(_e, v) =>
                                    setLongConnView((prev) => ({
                                      ...prev,
                                      [cfg.key]: v === 'sleeping' ? 'sleeping' : 'running'
                                    }))
                                  }
                                  sx={{ minHeight: 32 }}
                                >
                                  <Tab value="running" label={`In esecuzione (${topRunning.length})`} sx={{ minHeight: 32 }} />
                                  <Tab value="sleeping" label={`Sleep (${topSleeping.length})`} sx={{ minHeight: 32 }} />
                                </Tabs>

                                {bulkIds.length ? (
                                  <MuiTooltip title={`Termina tutte le connessioni > 10 minuti (${bulkIds.length})`}>
                                    <span>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        startIcon={<Trash2 size={16} />}
                                        onClick={() => setBulkKillState({ open: true, db: cfg.key, ids: bulkIds, view })}
                                        disabled={killLoading}
                                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                                      >
                                        &gt;10m ({bulkIds.length})
                                      </Button>
                                    </span>
                                  </MuiTooltip>
                                ) : null}
                              </Box>

                              <Box sx={{ maxHeight: 260, overflowY: 'auto', pr: 0.5 }}>
                                <Box sx={{ display: 'grid', gap: 0.75, mb: 1 }}>
                                  {(slice.length ? slice : []).map((r) => {
                                    const title =
                                      view === 'running'
                                        ? `${r.command || ''}${r.state ? ` · ${r.state}` : ''}${r.db ? ` · ${r.db}` : ''}`
                                        : `Sleep${r.db ? ` · ${r.db}` : ''}`;
                                    const details = `${title}${r.info ? `\n\n${r.info}` : ''}`;

                                    return (
                                      <MuiTooltip key={`${view}-${r.id}`} title={<span style={{ whiteSpace: 'pre-wrap' }}>{details}</span>}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Chip
                                            size="small"
                                            label={`${Math.round(safeNumber(r.time_seconds))}s`}
                                            sx={{
                                              bgcolor: view === 'running' ? 'rgba(211, 47, 47, 0.10)' : 'rgba(25, 118, 210, 0.10)',
                                              color: view === 'running' ? '#d32f2f' : cfg.color,
                                              fontWeight: 800
                                            }}
                                          />
                                          <Typography variant="body2" sx={{ fontWeight: 700, color: cfg.color }}>
                                            #{r.id}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                          >
                                            → {formatDurationSeconds(r.time_seconds)}
                                          </Typography>

                                          {view === 'running' ? (
                                            <>
                                              <MuiTooltip title="Termina query">
                                                <IconButton
                                                  size="small"
                                                  onClick={() => setKillState({ open: true, db: cfg.key, id: r.id, mode: 'query' })}
                                                >
                                                  <Square size={16} />
                                                </IconButton>
                                              </MuiTooltip>
                                              <MuiTooltip title="Termina connessione">
                                                <IconButton
                                                  size="small"
                                                  color="error"
                                                  onClick={() => setKillState({ open: true, db: cfg.key, id: r.id, mode: 'connection' })}
                                                >
                                                  <AlertTriangle size={16} />
                                                </IconButton>
                                              </MuiTooltip>
                                            </>
                                          ) : (
                                            <MuiTooltip title="Termina connessione">
                                              <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => setKillState({ open: true, db: cfg.key, id: r.id, mode: 'connection' })}
                                              >
                                                <AlertTriangle size={16} />
                                              </IconButton>
                                            </MuiTooltip>
                                          )}
                                        </Box>
                                      </MuiTooltip>
                                    );
                                  })}

                                  {!items.length ? (
                                    <Typography variant="body2" color="text.secondary">
                                      {view === 'running'
                                        ? 'Nessuna query/connessione in esecuzione ≥ 60s'
                                        : 'Nessuna connessione in Sleep ≥ 60s'}
                                    </Typography>
                                  ) : null}
                                </Box>
                              </Box>

                              {items.length > pageSize ? (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  <Pagination
                                    size="small"
                                    page={page}
                                    count={pages}
                                    onChange={(_e, p) =>
                                      setLongConnPages((prev) => ({
                                        ...prev,
                                        [cfg.key]: { ...prev[cfg.key], [view]: p }
                                      }))
                                    }
                                  />
                                </Box>
                              ) : null}
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      ) : null}
    </Box>
  );
}
